import { graphql, useStaticQuery } from 'gatsby';
import { Mutable } from 'gatsby-source-prismic';
import React, { FC } from 'react';
import { Defined, ExtractFromArray } from '../../../utlityTypes';
import Header from '../Header';
import { QueriedMegaDropdownSection, QueriedMegaDropdownSectionItem } from './types';

type HeaderProps = { id?: string | null };
/**
 * Header data component: specifies the fragment and passes the appropriate data to the Header component.
 */
const HeaderData: FC<HeaderProps> = ({ id }) => {
  const queryData: Queries.HeaderQuery = useStaticQuery(graphql`
    fragment HeaderItemLink on PrismicHeaderItemLink {
      primary {
        heading
        link {
          link_type
          url
        }
      }
    }

    fragment HeaderItemMegaDropdown on PrismicHeaderItemMegaDropdown {
      primary {
        heading
        leftSection {
          id
        }
        rightSection {
          id
        }
      }
    }

    query Header {
      allPrismicHeader {
        nodes {
          prismicId
          data {
            brandLogoLink {
              ...PrismicLink
            }
            megaDropdownBottomLink {
              ...PrismicLink
            }
            megaDropdownBottomText
            rightSidePrimaryButtonText
            rightSideSecondaryButton1Text
            rightSideSecondaryButton2Text
            rightSidePrimaryButtonLink {
              ...PrismicLink
            }
            rightSideSecondaryButton1Link {
              ...PrismicLink
            }
            rightSideSecondaryButton2Link {
              ...PrismicLink
            }
            slices {
              ...Slice
              ...HeaderItemLink
              ...HeaderItemMegaDropdown
            }
          }
        }
      }
      allPrismicHeaderMegaDropdownSection {
        nodes {
          prismicId
          data {
            heading
            text
            items {
              heading
              text
              link {
                ...PrismicLink
              }
            }
          }
        }
      }
    }
  `);

  const header = queryData.allPrismicHeader.nodes.find(({ prismicId }) => prismicId === id);
  type Slice = ExtractFromArray<Mutable<Defined<typeof header>['data']['slices']>>;
  const getMegaDropdownSection = (id?: string | null) =>
    queryData.allPrismicHeaderMegaDropdownSection.nodes.find(({ prismicId }) => prismicId === id) || null;

  const getLinkSlice = (slicePrimary: Queries.HeaderItemLinkFragment['primary']) => ({
    heading: slicePrimary.heading || '',
    link: slicePrimary.link,
  });

  const getMegaDropdownSectionItemProps = (item: QueriedMegaDropdownSectionItem) => ({
    heading: item.heading || '',
    text: item.text || '',
    link: item.link,
  });

  const getMegaDropdownSectionProps = (section: QueriedMegaDropdownSection | null) => ({
    heading: section?.data.heading || '',
    text: section?.data.text || '',
    items:
      section?.data.items
        ?.filter((item): item is Defined<typeof item> => !!item)
        .map(getMegaDropdownSectionItemProps) || [],
  });

  const getMegaDropdownSlice = (slicePrimary: Queries.HeaderItemMegaDropdownFragment['primary']) => ({
    heading: slicePrimary.heading || '',
    leftSection: getMegaDropdownSectionProps(getMegaDropdownSection(slicePrimary.leftSection?.id)),
    rightSection: getMegaDropdownSectionProps(getMegaDropdownSection(slicePrimary.rightSection?.id)),
  });

  const getSlice = (slice: Slice) =>
    'link' in slice.primary ? getLinkSlice(slice.primary) : getMegaDropdownSlice(slice.primary);

  const slices = header?.data.slices.map(getSlice) || [];

  return (
    <Header
      brandLogoLink={header?.data.brandLogoLink || null}
      megaDropdownCta={{
        text: header?.data.megaDropdownBottomText || '',
        link: header?.data.megaDropdownBottomLink || null,
      }}
      secondaryCta1={{
        text: header?.data.rightSideSecondaryButton1Text || '',
        link: header?.data.rightSideSecondaryButton1Link || null,
      }}
      secondaryCta2={{
        text: header?.data.rightSideSecondaryButton2Text || '',
        link: header?.data.rightSideSecondaryButton2Link || null,
      }}
      primaryCta={{
        text: header?.data.rightSidePrimaryButtonText || '',
        link: header?.data.rightSidePrimaryButtonLink || null,
      }}
      items={slices}
    />
  );
};

export default HeaderData;

export const query = graphql`
  fragment PageHeader on PrismicPageDataType {
    header {
      id
    }
  }

  fragment BlogPostHeader on PrismicBlogPostDataType {
    header {
      id
    }
  }

  fragment NotFoundPageHeader on Prismic404pageDataType {
    header {
      id
    }
  }
`;
